import { redirect, replace, type LoaderFunctionArgs, type ShouldRevalidateFunctionArgs } from 'react-router-dom';
import { PHONE_MEDIA_QUERY } from '@/lib/useIsPhone';

/**
 * Wie der Beleg-Editor (Verkauf und Beschaffung) erscheint — Wunsch 2026-09-30:
 * „Die Belegbearbeitung soll anfangs als Tab geöffnet werden, nicht gleich im
 * Max-Modus."
 *
 * - `tab`: `/verkauf/:id`, `/beschaffung/:id` — in der App-Hülle mit
 *   Seitenleiste, oberer Leiste und Tab-Leiste (ADR 0111). Der Standard.
 * - `vollbild`: `/verkauf/:id/editor`, `/beschaffung/:id/editor` — ohne Rahmen,
 *   wie bisher. Die Adressen bleiben, alte Links und Lesezeichen gelten weiter.
 *
 * Wer auf „Vollbild" schaltet, bekommt auch den nächsten Beleg im Vollbild;
 * „Verkleinern" setzt das zurück. Gemerkt je Gerät — eine Anzeige-Einstellung
 * wie Farbschema, Textgröße und Tabellendichte. Je Benutzer ginge es nicht
 * verlässlich: Die Entscheidung fällt im Router, bevor die App-Hülle rendert,
 * und beim Direktaufruf (Link aus einer Mail, Neuladen) ist dort noch
 * niemand angemeldet.
 *
 * Am Handy (unter `tablet`, 744 px) gibt es keine Tab-Leiste — dort öffnet der
 * Editor immer im Vollbild, wie bisher.
 */
export type EditorDisplayMode = 'tab' | 'vollbild';

export const EDITOR_FULLSCREEN_KEY = 'proerp:beleg-editor-vollbild';

const FULLSCREEN_SEGMENT = '/editor';

export function prefersFullscreenEditor(): boolean {
  try {
    return localStorage.getItem(EDITOR_FULLSCREEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function setPrefersFullscreenEditor(isPreferred: boolean): void {
  try {
    if (isPreferred) localStorage.setItem(EDITOR_FULLSCREEN_KEY, '1');
    else localStorage.removeItem(EDITOR_FULLSCREEN_KEY);
  } catch {
    // Best effort — ohne Storage gilt der Wunsch nur für diesen einen Wechsel.
  }
}

function isPhoneWidth(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(PHONE_MEDIA_QUERY).matches;
}

/** Geht ein Beleg, der jetzt geöffnet wird, im Vollbild auf? */
export function opensInFullscreen(): boolean {
  return isPhoneWidth() || prefersFullscreenEditor();
}

/**
 * Adresse desselben Belegs im anderen Modus. Query und Anker bleiben — darin
 * stehen Rückweg (`?back=`), Herkunft (`?from=liste`, Navigations-Audit
 * 2026-09-10) und Ansicht (`?ansicht=`).
 */
export function displayModePath(
  location: { pathname: string; search: string; hash: string },
  mode: EditorDisplayMode,
): string {
  const trimmed = location.pathname.replace(/\/+$/, '');
  const base = trimmed.endsWith(FULLSCREEN_SEGMENT) ? trimmed.slice(0, -FULLSCREEN_SEGMENT.length) : trimmed;
  return `${base}${mode === 'vollbild' ? FULLSCREEN_SEGMENT : ''}${location.search}${location.hash}`;
}

/**
 * Derselbe Beleg — egal ob Tab oder Vollbild, egal welche Ansicht oder welche
 * Marker? Umschalten ist kein Verlassen (Rückfrage bei Ungespeichertem: `EditorLeaveGuard`).
 */
export function isSameDocumentPath(pathname: string, otherPathname: string): boolean {
  const documentOf = (path: string) => displayModePath({ pathname: path, search: '', hash: '' }, 'tab');
  return documentOf(pathname) === documentOf(otherPathname);
}

/**
 * Loader von `/verkauf/:id` und `/beschaffung/:id`: Geht der Beleg im Vollbild
 * auf, leitet er um, BEVOR die App-Hülle rendert. Eine Umleitung aus der Seite
 * heraus (`<Navigate>`) ließe die Hülle kurz aufblitzen, und die Tab-Leiste
 * hätte dem Beleg schon einen Tab gegeben, obwohl er gar nicht im Tab steht.
 *
 * Verlauf: Beim Klick auf einen Link steht noch die alte Seite in der
 * Adresszeile — das Vollbild kommt als neuer Eintrag, Zurück führt zur Liste.
 * Steht die Adresse schon im Verlauf (Zurück/Vor, Direktaufruf, Neuladen),
 * ersetzt die Umleitung diesen Eintrag; ein zusätzlicher hielte „Zurück" fest
 * (zurück auf `/verkauf/:id` → wieder ins Vollbild → …).
 */
export function editorEntryLoader({ request }: LoaderFunctionArgs): Response | null {
  if (!opensInFullscreen()) return null;
  const url = new URL(request.url);
  const target = displayModePath({ pathname: url.pathname, search: url.search, hash: '' }, 'vollbild');
  const isCurrentEntry = window.location.pathname + window.location.search === url.pathname + url.search;
  return isCurrentEntry ? replace(target) : redirect(target);
}

/**
 * Nur beim Betreten eines Belegs entscheiden, nicht bei jeder Query-Änderung
 * (Belegblatt ↔ Werkbank): Hätte inzwischen ein anderes Browserfenster auf
 * „Vollbild" geschaltet, baute die Umleitung den Editor neu auf — und
 * Ungespeichertes wäre weg.
 */
export function shouldRevalidateEditorEntry({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs): boolean {
  return currentUrl.pathname !== nextUrl.pathname;
}
